import { FunctionThread, ModuleThread, Pool } from 'threads';

import { applyDefaultsToMessage } from 'src/transforms/applyDefaultsToMessage';
import { circularReplacer } from 'src/transforms/circularReplacer';
import { createBindingsTransform } from 'src/transforms/createBindingsTransform';
import { consoleTransporter } from 'src/transports/console';
import { logMessage } from 'src/logMessage';
import { LOGGER_LEVEL, LEVEL_NAME } from 'src/constants';
import { destroyTransports } from 'src/utils';
import {
    ChildOptions,
    CompleteLoggerConfig,
    ErrorPayload,
    ILogger,
    LoggerConfig,
    LoggerContext,
    MessagePayload,
} from 'src/types';

/**
 * Logger implementation for both the client and server.
 * Provides `console` style interface, which logs to the provided rollbar instances.
 *
 * Rollbar accept context provided to each logger method. Any data under
 * `context` will be passed to rollbar
 */
export class Logger implements ILogger {
    public config: CompleteLoggerConfig;

    pool: Pool<FunctionThread & ModuleThread> | null = null;

    constructor(config: LoggerConfig) {
        const transforms = config.transforms || [];
        const transports = config.transports || [];

        this.config = {
            threaded: false,
            ...config,
            transforms,
            transports,
            logLevel: LEVEL_NAME[config.level],
        };

        // Always ensure that applyDefaultsToMessage
        // is the last transforms run
        // TODO force to rear if present
        if (!transforms.includes(applyDefaultsToMessage)) {
            transforms.push(applyDefaultsToMessage);
        }

        if (!transforms.includes(circularReplacer)) {
            transforms.push(circularReplacer);
        }

        if (!transports.length) {
            transports.push(consoleTransporter);
        }
    }

    public child(options: ChildOptions = {}): ILogger {
        const {
            name,
            level = this.config.level,
            transforms = [],
            transports = [],
            ...bindings
        } = options;

        transforms.push(createBindingsTransform(bindings));

        const config = {
            ...this.config,
            name,
            level,
            threaded: false,
            transforms: transforms.concat(this.config.transforms),
            transports: transports.concat(this.config.transports),
        };

        const child = new Logger(config);

        child.destroy = async (): Promise<void> => destroyTransports(transports);
        child.log = this.log.bind(child);

        return child;
    }

    public async destroy(force = false): Promise<void> {
        if (this.pool) {
            await this.pool.terminate(force);
        }

        const { transports } = this.config;

        await destroyTransports(transports);
    }

    /**
   * Logs the provided message with logging level CRITICAL.
   * Calls {@see logMessage}. If <code>error</code> is provided, the error
   * object is sent to rollbar along with the message.
   *
   * @param message Message may be a string or an object
   * @param error   The thrown error corresponding to this error log
   * @param context Additional context to provide to rollbar
   */
    public async critical(
        message: MessagePayload | Error,
        error?: ErrorPayload,
        context?: LoggerContext,
    ): Promise<null> {
        return this.log(LOGGER_LEVEL.CRITICAL, message, context, error);
    }

    /**
   * Logs the provided message with logging level DEBUG.
   * Calls {@see logMessage}
   *
   * @param message Message may be a string or an object
   * @param context Additional context to provide to rollbar
   */
    public async debug(
        message: MessagePayload,
        context?: LoggerContext,
    ): Promise<null> {
        return this.log(LOGGER_LEVEL.DEBUG, message, context);
    }

    /**
   * Logs the provided message with logging level ERROR.
   * Calls {@see logMessage}. If <code>error</code> is provided, the error
   * object is sent to rollbar along with the message.
   *
   * @param message Message may be a string or an object
   * @param error   The thrown error corresponding to this error log
   * @param context Additional context to provide to rollbar
   */
    public async error(
        message: MessagePayload | Error,
        error?: ErrorPayload,
        context?: LoggerContext,
    ): Promise<null> {
    // if the first parameter is an error, pass into logMessage appropriately
        let err = error;
        let msg = message;

        if (message instanceof Error) {
            err = message;
            msg = err.message;
        }

        return this.log(LOGGER_LEVEL.ERROR, msg, context, err);
    }

    /**
   * Logs the provided message with logging level INFO.
   * Calls {@see logMessage}
   *
   * @param message Message may be a string or an object
   * @param context Additional context to provide to rollbar
   */
    public async info(
        message: MessagePayload,
        context?: LoggerContext,
    ): Promise<null> {
        return this.log(LOGGER_LEVEL.INFO, message, context);
    }

    /**
   * Given a <code>level</code> and a <code>message</code> (at minimum),
   * <code>log</code> first sends the <code>message</code> as a string to
   * <code>rollbar</code>, along with <code>context</code> and
   * <code>error</code> if provided.
   * After logging to <code>rollbar</code>, <code>log</code> formats the
   * <code>message</code> into an object if it's not already one.
   *
   * @param level
   * @param message
   * @param context
   * @param error
   */
    public async log(
        level: LOGGER_LEVEL,
        message: MessagePayload | Error,
        // eslint-disable-next-line default-param-last
        context: LoggerContext = {} as LoggerContext,
        error?: ErrorPayload,
    ): Promise<null> {
        return logMessage(level, message, context, error, this);
    }

    /**
   * Logs the provided message with logging level WARNING.
   * Calls {@see logMessage}. If <code>error</code> is provided, the error object is
   * sent to rollbar along with the message.
   *
   * @param message Message may be a string or an object
   * @param error   (optional) The thrown error corresponding to this warning
   * @param context Additional context to provide to rollbar
   */
    public async warning(
        message: MessagePayload | Error,
        error?: ErrorPayload,
        context?: LoggerContext,
    ): Promise<null> {
        return this.log(LOGGER_LEVEL.WARNING, message, context, error);
    }
}
